import { useSdk } from '~/sdk';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { Reactive } from 'vue';
import type {
  CreateAddressRequest,
  GetAddressesResponse,
  RemoveAddressRequest,
  UpdateAddressRequest,
  UpdateAddressResponse,
  UpdateCheckoutAddressRequest,
  GetAddressesResponseItem
} from '@vue-storefront/prestashop-api';

export const useAddresses = () => {
  const { data: cart } = useCart();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const sdk = useSdk();

  const query = useQuery({
    queryKey: ['addresses'],
    queryFn: async (): Promise<GetAddressesResponse['psdata']> => {
      return await sdk.commerce.getAddresses();
    },
    enabled: auth.isAuthenticated
  });

  const mutationSelect = useMutation({
    mutationFn: async (args: UpdateCheckoutAddressRequest) => {
      let res = await sdk.commerce.updateCheckoutAddress({
        id_address_delivery: unref(args.id_address_delivery),
        id_address_invoice: unref(args.id_address_invoice) ?? unref(args.id_address_delivery),
      });

      // after changing address we reset the selected pudo
      await sdk.commerce.updateCheckoutCarrier({
        id_address: unref(args.id_address_delivery),
        id_carrier: 1, // we also reset the carrier
        selected_pudo: null,
      });

      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['pudos'] });
    },
  });

  const mutationAdd = useMutation({
    mutationFn: async (address: MaybeRef<CreateAddressRequest>) => {
      return await sdk.commerce.createAddress(unref(address));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  const mutationUpdate = useMutation({
    mutationFn: async (data: MaybeRef<UpdateAddressRequest>) => {
      return await sdk.commerce.updateAddress(unref(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['pudos'] });
    },
  });

  const mutationRemove = useMutation({
    mutationFn: async (data: MaybeRef<RemoveAddressRequest>) => {
      return await sdk.commerce.removeAddress(unref(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  return {
    query: reactive(query) satisfies Reactive<typeof query>,
    mutationAdd: reactive(mutationAdd) satisfies Reactive<typeof mutationAdd>,
    mutationUpdate: reactive(mutationUpdate) satisfies Reactive<typeof mutationUpdate>,
    mutationRemove: reactive(mutationRemove) satisfies Reactive<typeof mutationRemove>,
    mutationSelect: reactive(mutationSelect) satisfies Reactive<typeof mutationSelect>,
    currentAddressDelivery: computed(() => {
      return query.data?.value?.[cart.value?.id_address_delivery] ?? null;
    }),
    currentAddressInvoice: computed(() => {
      return query.data?.value?.[cart.value?.id_address_invoice] ?? null;
    }),
  };
};
